// Adapter over the typed `useLinkedinAccounts` hook (api-client) for the
// businesses feature. It flattens the `{ accounts: LinkedinAccountRow[] }`
// payload to the `{ id, name, color, initials }` summary the businesses UI
// needs, and exposes a small `{ data, isLoading, isError, refetch }` surface
// so callers don't depend on react-query internals.

import { useLinkedinAccounts } from '@barel/api-client/react';

export interface LinkedinAccountSummary {
  id: string;
  name: string;
  color?: string;
  initials?: string;
}

export interface UseLinkedinAccountsListResult {
  data: LinkedinAccountSummary[];
  isLoading: boolean;
  isError: boolean;
  /** Re-run the accounts query (used to recover from a load error). */
  refetch: () => void;
}

export function useLinkedinAccountsList(): UseLinkedinAccountsListResult {
  const query = useLinkedinAccounts();
  // The hook returns the typed `{ accounts: [...] }` payload; tolerate a bare
  // array too in case the shape is ever simplified upstream.
  const raw = (query.data ?? null) as
    | null
    | { accounts?: LinkedinAccountSummary[] }
    | LinkedinAccountSummary[];

  let accounts: LinkedinAccountSummary[] = [];
  if (Array.isArray(raw)) {
    accounts = raw;
  } else if (raw && Array.isArray(raw.accounts)) {
    accounts = raw.accounts;
  }

  return {
    data: accounts,
    isLoading: Boolean(query.isLoading),
    isError: Boolean(query.isError),
    refetch: () => {
      void query.refetch();
    },
  };
}
